import {Injectable} from '@angular/core'
import {map, Observable, switchMap} from 'rxjs'
import {CachedAPIResponse} from '../interfaces'

@Injectable({
  providedIn: 'root',
})
export class CacheService {
  private readonly dbName = 'cacheDB'
  private readonly storeName = 'cacheStore'

  generateCacheKey(url: string, method: string, body: any) {
    const bodyStr = JSON.stringify(body)
    return `${url}|${method}|${bodyStr}`
  }

  private openDatabase(): Observable<IDBDatabase> {
    return new Observable<IDBDatabase>((subscriber) => {
      const request = indexedDB.open(this.dbName, 1)

      request.onupgradeneeded = (event) => {
        const db = (event.target as IDBOpenDBRequest).result
        if (!db.objectStoreNames.contains(this.storeName)) {
          db.createObjectStore(this.storeName, { keyPath: 'key' })
        }
      }

      request.onsuccess = (event) => {
        subscriber.next((event.target as IDBOpenDBRequest).result)
        subscriber.complete()
      }

      request.onerror = (event) => {
        subscriber.error((event.target as IDBOpenDBRequest).error)
      }
    })
  }

  get(key: string): Observable<any | null> {
    return this.openDatabase().pipe(
      switchMap((db) => {
        return new Observable<CachedAPIResponse | null>((subscriber) => {
          const transaction = db.transaction(this.storeName, 'readonly')
          const store = transaction.objectStore(this.storeName)
          const request = store.get(key)

          request.onsuccess = (event) => {
            const cached = (event.target as IDBRequest<CachedAPIResponse>).result

            if (!cached) {
              subscriber.next(null)
              subscriber.complete()
              return
            }

            const now = Date.now()
            if (now - cached.timestamp > cached.ttl) {
              this.clear(key).subscribe(() => {
                subscriber.next(null)
                subscriber.complete()
              })
              return
            }

            subscriber.next(cached)
            subscriber.complete()
          }

          request.onerror = () => {
            subscriber.next(null)
            subscriber.complete()
          }
        })
      }),
      map((cached) => (cached ? cached.response : null))
    )
  }

  put(key: string, response: any, ttl: number): Observable<void> {
    return this.openDatabase().pipe(
      switchMap((db) => {
        return new Observable<void>((subscriber) => {
          const transaction = db.transaction(this.storeName, 'readwrite')
          const store = transaction.objectStore(this.storeName)
          const cachedResponse: CachedAPIResponse = { key, response, timestamp: Date.now(), ttl }
          const request = store.put(cachedResponse)

          request.onsuccess = () => {
            subscriber.next()
            subscriber.complete()
          }

          request.onerror = (event) => {
            subscriber.error((event.target as IDBRequest).error)
          }
        })
      })
    )
  }

  clear(key: string): Observable<void> {
    return this.openDatabase().pipe(
      switchMap((db) => {
        return new Observable<void>((subscriber) => {
          const transaction = db.transaction(this.storeName, 'readwrite')
          const store = transaction.objectStore(this.storeName)
          const request = store.delete(key)

          request.onsuccess = () => {
            subscriber.next()
            subscriber.complete()
          }

          request.onerror = (event) => {
            subscriber.error((event.target as IDBRequest).error)
          }
        })
      })
    )
  }

  clearKeyLike(key: string): Observable<void> {
    return this.openDatabase().pipe(
      switchMap((db) => {
        return new Observable<void>((subscriber) => {
          const transaction = db.transaction(this.storeName, 'readwrite')
          const store = transaction.objectStore(this.storeName)
          const request = store.openCursor()

          request.onsuccess = (event) => {
            const cursor = (event.target as IDBRequest<IDBCursorWithValue>).result

            if (cursor) {
              if (cursor.key.toString().includes(key)) {
                store.delete(cursor.key)
              }

              cursor.continue()
            } else {
              subscriber.next()
              subscriber.complete()
            }
          }

          request.onerror = (event) => {
            subscriber.error((event.target as IDBRequest).error)
          }
        })
      })
    )
  }

  clearAll(): Observable<void> {
    return this.openDatabase().pipe(
      switchMap((db) => {
        return new Observable<void>((subscriber) => {
          const transaction = db.transaction(this.storeName, 'readwrite')
          const store = transaction.objectStore(this.storeName)
          const request = store.clear()

          request.onsuccess = () => {
            subscriber.next()
            subscriber.complete()
          }

          request.onerror = (event) => {
            subscriber.error((event.target as IDBRequest).error)
          }
        })
      })
    )
  }
}
